import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';
import { isCasinoGamePage } from 'utils';

import {
  AccountManager,
  btag,
  printMissingConfigError,
  pushToDataLayer,
  SiteConfigManager,
  useApplicationState,
  useSettings,
} from '@atlas/core';
import { COMPONENT_TYPES } from '@atlas/routing';

import { useLocalBetslip } from 'common/Betslip/LocalBetslipContext';
import { PLATFORM_TYPES } from 'common/constants';
import MenuFooterLinkList from 'components/Footer/MenuFooterLinkList';
import useLiveChat from 'components/LiveChat/useLiveChat';
import SeoPageText from 'components/SeoPage/SeoPageText';
import StaticBlock from 'src/common/StaticBlock/StaticBlock';
import useSignInRedirects from 'src/components/Auth/useSignInRedirects';

import MenuItemChild from './MenuItemContent/MenuItemChild';
import MenuList from './MenuList/MenuList';
import MenuSportsListView from './MenuSportsList/MenuSportsListView';

const { MOBILE, MULTIPLATFORM } = PLATFORM_TYPES;

const { CASINO, CASINO_LIVE } = COMPONENT_TYPES;

const Menu = () => {
  const { authenticated } = useApplicationState();
  const { quickBet: isQuickBet } = useSettings();
  const { betIdToEdit } = useLocalBetslip();
  const { toggleIsOpened: toggleIsChatOpened } = useLiveChat();
  const { redirectToSignIn } = useSignInRedirects();
  const history = useHistory();
  const {
    location: { state = {} },
    push: historyPush,
  } = history;

  React.useEffect(() => {
    if (state?.redirectPath) {
      history.replace(state.redirectPath);
    }
  }, [state?.redirectPath]);

  const isChatAvailable = SiteConfigManager.getConfig(
    'config.config.support.live_chat.mobile.is_available',
  );

  const liveChatAltIcon = SiteConfigManager.getConfig(
    'config.config.support.live_chat.mobile.alt_icon',
  );

  const liveChatIconType = SiteConfigManager.getConfig(
    'config.config.support.live_chat.mobile.icon_type',
  );

  const liveChatIcon = useMemo(() => {
    if (liveChatIconType === 'predefined') return 'i-chat';

    return SiteConfigManager.getConfig('config.config.support.live_chat.mobile.icon');
  }, [liveChatIconType]);

  const availableMenuItems = useMemo(() => {
    const casinoPath = SiteConfigManager.getComponentPath(CASINO);
    const casinoLivePath = SiteConfigManager.getComponentPath(CASINO_LIVE);

    const navigation = SiteConfigManager.getConfig('navigation.top', [])
      .filter(el => el.platform_type === MULTIPLATFORM || el.platform_type === MOBILE)
      .sort((a, b) => a.order_index - b.order_index)
      .map(({ alt_icon: altIcon, children, icon, icon_type: iconType, location, name }) => {
        const isCasinoGame = isCasinoGamePage(location, [casinoPath, casinoLivePath]);

        if (isCasinoGame) {
          return {
            icon,
            iconType,
            name,
            noLink: true,
            onClick: () => {
              if (!authenticated) {
                redirectToSignIn({
                  redirectPath: location,
                });
                return;
              }

              historyPush(location);
            },
          };
        }

        return {
          altIcon,
          icon,
          iconType,
          link: location,
          name,
          subpages: children,
        };
      });

    if (isChatAvailable) {
      navigation.push({
        altIcon: liveChatAltIcon,
        icon: liveChatIcon,
        iconType: liveChatIconType,
        isSupportIcon: true,
        name: 'SUPPORT',
        onClick: toggleIsChatOpened,
      });
    }

    return navigation;
  }, [
    isChatAvailable,
    authenticated,
    redirectToSignIn,
    liveChatAltIcon,
    liveChatIcon,
    liveChatIconType,
    toggleIsChatOpened,
  ]);

  const [activeItem, setActiveItem] = useState(null);

  const onSelectMenuItem = useCallback(name => setActiveItem(name), [setActiveItem]);

  const handleMenuBannerClick = useCallback(() => {
    const { clientId } = AccountManager.account;

    pushToDataLayer('mobile_menu_banner_click', { client: { id: clientId } }, btag);
  }, []);

  const layoutToScrollClass = cx('layout-to-scroll', { quickbet: isQuickBet || betIdToEdit });

  if (
    isChatAvailable &&
    (!liveChatIconType || (liveChatIconType === 'custom' && !liveChatAltIcon && !liveChatIcon))
  ) {
    printMissingConfigError('config.config.support.live_chat.mobile[alt_icon|icon_type|icon]');
  }

  return (
    <div className={layoutToScrollClass}>
      <div className="menu-page">
        <MenuList
          activeItem={activeItem}
          menuItems={availableMenuItems}
          onSelectMenuItem={onSelectMenuItem}
        />
        <StaticBlock
          className="menu-banner"
          onClick={handleMenuBannerClick}
          options={{ source: 'mobile_menu_banner' }}
        />
        <MenuSportsListView />
        <MenuItemChild activeItem={activeItem} />
        <MenuFooterLinkList />
        <SeoPageText />
      </div>
    </div>
  );
};

export default Menu;
